import { ApolloClient, ApolloProvider, InMemoryCache } from "@apollo/client";
import CssBaseline from "@material-ui/core/CssBaseline";
import TemplatesPage from "pages/creative/components/templates/";
import SettingsPage from "pages/settings";
import DynamicCouponsQrPage from "pages/settings/dynamic-coupons-qr";
import { ThemeProvider } from "@material-ui/core/styles";
import { ApolloLink, concat } from "apollo-link";
import { HttpLink } from "apollo-link-http";
import { QueryClient, QueryClientProvider } from "react-query";
import CampaignDataHOC from "campaign-data-hoc";
import DMCampaignDataHOC from "dm-campaign-data-hoc";
import { DialogModal } from "components/dialog-modal";
import ProgressBar from "components/progress-bar";
import SnackBar from "components/snack-bar";
import ROUTING, { EDDM_ROUTING, ADM_ROUTING, SETTINGS } from "config/routing";
import Auth from "pages/auth";
import OAuthHandover from "pages/auth/components/oauth-callback";
import AccessDenied from "pages/access-denied";
import Builder from "pages/flyer/builder";
import PublicFlyerBuilderPage from "pages/flyer/builder/PublicBuilder";
import { CampaignBuilderFigmaEditGate } from "pages/flyer/builder/FigmaEditGate";
import SelectFlyerTypePage from "pages/flyer/flyerTypePage";
import { Error } from "pages/error";
import { Segments } from "pages/eddm/segments";
import DesignUpload from "pages/creative/components/upload/DesignUpload";
import { CampaignBuilder } from "campaign-builder/CampaignBuilder";
import { CampaignDataProvider } from "campaign-builder/context/CampaignDataContext";
import { ClientConfigProvider } from "campaign-builder/context/ClientConfigContext";
import CONFIG from "config/config";
import { FeatureFlagContextProvider } from "context/featureFlagContext";
import ClientChat from "copilot/ClientChat";
import { BrowserRouter as Router, Redirect, Route, Switch } from "react-router-dom";
import { EDDMSubmitContainer } from "pages/eddm/submit";
import GlobalStyles from "./global-styles";
import { loadGTM } from "./loadGTM";
import "./style.css";
import "./tailwind-output.css";

import theme from "./theme";
import Login from "./pages/auth/components/login";

const httpLink = new HttpLink({ uri: CONFIG.FLYER_API_URL });

const authMiddleware = new ApolloLink((operation, forward) => {
  // add the authorization to the headers
  const token = localStorage.getItem("apiKey");
  operation.setContext({
    headers: {
      authorization: token ? `Bearer ${token}` : "",
    },
  });
  return forward(operation);
});

const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: concat(authMiddleware, httpLink),
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  },
});

loadGTM("TVBX5KQ", "https://sgtm.oppizi.com/pqtwjlgp.js?st=TVBX5KQ");

export const NewApp = () => {
  return (
    <ApolloProvider client={client}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles />
        <DialogModal>
          <SnackBar>
            <ProgressBar>
              <Auth>
                <Router>
                  {/* Global Copilot chat panel — one instance for the whole app, shown
                      to client users; header launchers toggle it via a DOM event. */}
                  <ClientChat>
                    <Switch>
                      {/* COMMON ROUTES */}
                      <Route path={ROUTING.LOGIN} component={Login} /> {/* TODO: SHOW LOGIN ONLY FOR DEVS */}
                      <Route path={[ROUTING.ERROR]} component={Error} />
                      <Route path={ROUTING.ACCESS_DENIED} component={AccessDenied} />
                      {/* OAuth handover landings — render inside the popup window opened
                        by SignupModal. They post the URL params back to the opener and
                        close themselves. */}
                      <Route exact path={ROUTING.OAUTH_SOCIAL_SIGNUP} render={() => <OAuthHandover kind="signup" />} />
                      <Route exact path={ROUTING.OAUTH_SOCIAL_CALLBACK} render={() => <OAuthHandover kind="login" />} />
                      <Route exact path={ROUTING.PUBLIC_FLYER_BUILDER_TEMPLATE} component={TemplatesPage} />
                      <Route
                        exact
                        path={[ROUTING.PUBLIC_FLYER_BUILDER, ROUTING.PUBLIC_FLYER_BUILDER_NEW]}
                        component={PublicFlyerBuilderPage}
                      />
                      <Route exact path={ROUTING.UPLOAD_DESIGN} component={DesignUpload} />
                      {/* SETTINGS ROUTES */}
                      <Route path={SETTINGS.SETTINGS}>
                        <Switch>
                          <Route exact path={SETTINGS.SETTINGS} component={SettingsPage} />
                          <Route path={SETTINGS.SETTINGS_DYNAMIC_COUPONS_QR} component={DynamicCouponsQrPage} />
                          <Redirect to={ROUTING.ERROR} />
                        </Switch>
                      </Route>
                      {/* EDDM ROUTES */}
                      <Route path={EDDM_ROUTING.EDDM_CAMPAIGN}>
                        <DMCampaignDataHOC>
                          <FeatureFlagContextProvider>
                            <Switch>
                              {/* Redirect old Details route to Flyer (Design) page */}
                              <Route
                                path={EDDM_ROUTING.EDDM_CAMPAIGN_DETAILS}
                                render={({ match }) => (
                                  <Redirect
                                    to={`/client/${match.params.clientId}/eddm/flyer/${match.params.campaignId}`}
                                  />
                                )}
                              />
                              <Route path={EDDM_ROUTING.EDDM_CAMPAIGN_SEGMENTS} component={Segments} />
                              <Route
                                path={[EDDM_ROUTING.EDDM_SUBMIT, EDDM_ROUTING.EDDM_SUCCESS]}
                                component={EDDMSubmitContainer}
                              />
                              <Redirect to={ROUTING.ERROR} />
                            </Switch>
                          </FeatureFlagContextProvider>
                        </DMCampaignDataHOC>
                      </Route>
                      <Route path={ADM_ROUTING.ADM_CAMPAIGN}>
                        <Switch>
                          {/* Builder URL unified to /campaign/:id/builder for every channel
                            (CampaignBuilder dispatches by the loaded campaign's channel).
                            Redirect the legacy /adm/ builder URLs (and the old builder-v2)
                            so existing links and bookmarks keep resolving. */}
                          <Redirect from={ADM_ROUTING.ADM_CAMPAIGN_BUILDER_V2} to={ROUTING.CAMPAIGN_BUILDER} />
                          <Redirect from={ADM_ROUTING.ADM_CAMPAIGN_BUILDER} to={ROUTING.CAMPAIGN_BUILDER} />
                          <Redirect to={ROUTING.ERROR} />
                        </Switch>
                      </Route>
                      <Route path={EDDM_ROUTING.EDDM_FLYER}>
                        <DMCampaignDataHOC>
                          <FeatureFlagContextProvider>
                            <Switch>
                              <Route path={EDDM_ROUTING.EDDM_FLYER_BUILDER_TEMPLATE} component={TemplatesPage} />
                              <Route path={EDDM_ROUTING.EDDM_FLYER_BUILDER} component={Builder} />
                              <Route path={EDDM_ROUTING.EDDM_UPLOAD_FLYER} component={SelectFlyerTypePage} />
                              <Route path={EDDM_ROUTING.EDDM_FLYER} component={SelectFlyerTypePage} />
                            </Switch>
                          </FeatureFlagContextProvider>
                        </DMCampaignDataHOC>
                      </Route>
                      {/* H2H and D2D — redesigned 3-step builder (parallel to the legacy flow
                        below; reachable directly until the platform repoints its cards). */}
                      <Route path={ROUTING.CAMPAIGN_BUILDER}>
                        <QueryClientProvider client={queryClient}>
                          <FeatureFlagContextProvider>
                            <CampaignDataProvider>
                              <ClientConfigProvider>
                                <Switch>
                                  <Route path={ROUTING.CAMPAIGN_BUILDER} component={CampaignBuilder} />
                                  <Redirect to={ROUTING.ERROR} />
                                </Switch>
                              </ClientConfigProvider>
                            </CampaignDataProvider>
                          </FeatureFlagContextProvider>
                        </QueryClientProvider>
                      </Route>
                      {/* Legacy non-DM flyer flow (ROUTING.FLYER) removed: H2H/D2D are
                        migrated to the redesigned builder (CAMPAIGN_BUILDER above) and
                        platform now links there. EDDM keeps its own flow (EDDM_FLYER). */}
                      {/* The tldraw flyer builder itself is NOT legacy — the redesigned
                        builder's design step opens designs here (edit / create / add
                        address box), so this route must outlive the flow above. */}
                      <Route path={ROUTING.FLYER_BUILDER}>
                        <CampaignDataHOC>
                          <FeatureFlagContextProvider>
                            <CampaignBuilderFigmaEditGate>
                              <Builder />
                            </CampaignBuilderFigmaEditGate>
                          </FeatureFlagContextProvider>
                        </CampaignDataHOC>
                      </Route>
                      {/* Unmatched URLs (incl. deleted legacy /campaign/* step routes)
                        land on the Error page instead of a blank screen. */}
                      <Redirect to={ROUTING.ERROR} />
                    </Switch>
                  </ClientChat>
                </Router>
              </Auth>
            </ProgressBar>
          </SnackBar>
        </DialogModal>
      </ThemeProvider>
    </ApolloProvider>
  );
};
